import { Desktop, LargeMobile, SmallMobile } from 'styles/MediaQueries';
import { GRAY, IRON, WHITE_BACKGROUND } from 'styles/Colors';
import { OPPLOANS } from 'helpers/Constants';
import { validateInfo } from 'registration/lead/gate/LeadGateApiCalls';
import BasicInput from '@opploans/basic-input-component/BasicInput';
import Checkbox from 'components/forms/Checkbox';
import CtaButton from '@opploans/cta-button-component/CtaButton';
import DynamicFooter from 'components/display/DynamicFooter';
import NumberFormat from 'react-number-format';
import PartnerBranding from 'components/rebrand/PartnerBranding';
import React, { forwardRef, memo, useEffect, useState } from 'react';
import SiteHeader from '@opploans/site-header-component/SiteHeader';

const appConfig = window.AppConfig || {};

const containerStyle = {
  display: 'grid',
  minHeight: '100vh',

  '.MuiFormControl-root': {
    width: '100%'
  },
  '.MuiTooltip-popper': {
    width: '100%'
  },
  [Desktop]: {
    backgroundSize: '20px 20px'
  }
};

const contentStyle = {
  margin: '2rem auto',
  padding: '0 1.25rem',

  ' > *:not(:last-child)': {
    marginBottom: '1.25rem'
  },
  [Desktop]: {
    width: '30rem'
  },
  [SmallMobile]: {
    width: '80%'
  },
  [LargeMobile]: {
    maxWidth: '30rem',
    width: '80%'
  }
};

const additionalButtonStyle = {
  fontSize: '100%',
  height: '3.2rem',
  width: '100%',

  '&:disabled, &:disabled:hover': {
    background: GRAY,
    color: IRON
  }
};

const partnerBrandingWrapperStyle = {
  background: WHITE_BACKGROUND,

  '> div': contentStyle
};

const errorStyle = {
  color: 'red',
  fontSize: '0.875rem',
  textAlign: 'center'
};

const Footer = memo(DynamicFooter);

function Cobranding() {
  const partnerData = window.AppConfig.partner_data || {};
  const partnerId = partnerData.id;
  const partnerName = partnerData.partner_name;

  if (!partnerId) {
    return null;
  }

  return (
    <PartnerBranding
      alternateTertiaryText='Confirm your information to continue to your loan application.'
      alternateSecondaryText=''
      partnerId={partnerId}
      partnerName={partnerName}
    />
  );
}

// not including ref argument results in a console warning
/* eslint no-unused-vars: ["error", { "args": "none" }] */
const Last4SsnMask = forwardRef(({ onChange, ...rest }, ref) => {
  const handleValueChange = (values) => {
    onChange({ target: { value: values.value } });
  };

  return (
    <NumberFormat
      {...rest}
      format='####'
      isNumericString
      onValueChange={handleValueChange}
    />
  );
});

const LeadGate = () => {
  const partner = appConfig.partner || {};

  useEffect(() => {
    window.postHeapAnalytic('track', '[lead_gate][render]', {
      lead_id: partner.lead_id,
      lead_offer_id: partner.partner_event_data?.lead_offer_id,
      origination_id: window.AppConfig.origination_id,
      show_acknowledgement: window.AppConfig.show_acknowledgement
    });
  }, []);

  const provider = appConfig.cl_application?.product_owner || OPPLOANS;

  const [checked, setChecked] = useState(
    !window.AppConfig.show_acknowledgement
  );
  const [lastName, setLastName] = useState('');
  const [last4Ssn, setLast4Ssn] = useState('');
  const [last4SsnError, setLast4SsnError] = useState(null);
  const [lastNameError, setLastNameError] = useState(null);
  const [showBackendError, setShowBackendError] = useState(false);
  const [loading, setLoading] = useState(false);

  const lastNameOnChange = (val) => {
    setLastName(val);
    if (val === '') {
      setLastNameError('Must enter last name');
    } else {
      setLastNameError(null);
    }
    setShowBackendError(false);
  };

  const last4SsnOnChange = (val) => {
    setLast4Ssn(val);
    if (val.length !== 4) {
      setLast4SsnError('Must enter last 4 of SSN');
    } else {
      setLast4SsnError(null);
    }
    setShowBackendError(false);
  };

  const handleSubmit = async () => {
    setLoading(true);

    const response = await validateInfo(lastName, last4Ssn);
    if (!response || response.status > 299) {
      // error has been already handled by validateInfo function
      setLoading(false);
      return;
    }

    const redirectPath = response.data?.redirect_path;
    if (redirectPath) {
      window.postHeapAnalytic('track', '[lead_gate][redirect]', {
        redirect_path: redirectPath
      });
      window.location.href = `${redirectPath}${window.location.search}`;
    } else {
      window.postHeapAnalytic('track', '[lead_gate][validation_failure]', {
        last_name: lastName,
        last_4_ssn: last4Ssn
      });
      setShowBackendError(true);
      setLoading(false);
    }
  };

  return (
    <div css={containerStyle}>
      {/* I can't figure out why, but something about this page makes the
          SiteHeader component taller than it should be.
          So let's contain it. */}
      <div css={{ height: '4rem' }}>
        <SiteHeader provider={provider} />
      </div>

      <div css={partnerBrandingWrapperStyle}>
        <Cobranding />
      </div>

      <div css={contentStyle}>
        <BasicInput
          id='lastName'
          label='Last Name'
          maxLength={40}
          onChange={lastNameOnChange}
          error={lastNameError}
          value={lastName || ''}
          isVisible
        />

        <BasicInput
          id='lastFourSsn'
          label='Last 4 of Social Security Number'
          isNumeric
          InputProps={{
            inputComponent: Last4SsnMask
          }}
          onChange={last4SsnOnChange}
          isVisible
          error={last4SsnError}
          value={last4Ssn || ''}
        />

        {showBackendError && (
          <p css={errorStyle}>
            One or more fields did not match our records. Try Again
          </p>
        )}

        {window.AppConfig.show_acknowledgement && (
          <Checkbox
            id='acknowledgement'
            name='acknowledgement'
            label="I acknowledge that the loan terms, such as interest rate, loan amount, and repayment terms, are based on the originating bank's underwriting requirements and are subject to change after a full review of my loan application. Generally, APR ranges from 160% to 195%."
            value={checked}
            onChange={(e) => setChecked(e.target.checked)}
            additionalLabelStyle={{ fontSize: '0.875rem' }}
          />
        )}

        <CtaButton
          additionalStyle={additionalButtonStyle}
          id='elecCommModalOpen'
          loading={loading}
          onClick={handleSubmit}
          disabled={
            !(lastName && last4Ssn && checked) || lastNameError || last4SsnError
          }
        >
          Confirm
        </CtaButton>
      </div>

      <Footer additionalFooterStyle={{ justifySelf: 'flex-end' }} />
    </div>
  );
};

export default LeadGate;
